import React, { useState, useEffect, useCallback } from 'react';
import {
    ShieldCheck, AlertTriangle, Bug, Activity,
    Loader2, XCircle, Clock, Info,
} from 'lucide-react';
import { adminAnalytics } from '../../services/api';
import MetricCard from './MetricCard';
import MetricDrawer from './MetricDrawer';
import BreakdownTable from './BreakdownTable';

/**
 * HealthTab — System health: pipeline success/failure, error events, breakdown.
 *
 * Props:
 *   filters: { days, compare, district_id, campus_id }
 */

const HealthTab = ({ filters = {} }) => {
    const [data, setData] = useState(null);
    const [genTime, setGenTime] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [drawerMetric, setDrawerMetric] = useState(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = {
                days: filters.days || 7,
                ...(filters.district_id && { district_id: filters.district_id }),
                ...(filters.campus_id && { campus_id: filters.campus_id }),
            };
            const [healthRes, genTimeRes] = await Promise.allSettled([
                adminAnalytics.getHealth(params),
                adminAnalytics.getGenerationTime({ days: filters.days || 7 }),
            ]);
            if (healthRes.status === 'fulfilled') setData(healthRes.value.data);
            else throw new Error(healthRes.reason?.response?.data?.detail || 'Failed to load health data.');
            if (genTimeRes.status === 'fulfilled') setGenTime(genTimeRes.value.data);
        } catch (err) {
            setError(err.message || 'Failed to load health data.');
        } finally {
            setLoading(false);
        }
    }, [filters.days, filters.district_id, filters.campus_id]);

    useEffect(() => { loadData(); }, [loadData]);

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
                <span style={{ fontSize: 13, color: '#6b7280' }}>Loading health data…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <AlertTriangle size={24} style={{ color: '#ef4444' }} />
                <p style={{ fontSize: 14, color: '#ef4444', fontWeight: 500, margin: 0 }}>{error}</p>
            </div>
        );
    }

    const pipeline = data?.pipeline || {};
    const systemEvents = data?.system_events || {};
    const successRate = pipeline.success_rate_pct ?? 100;
    const successColor = successRate >= 99 ? '#10b981' : successRate >= 95 ? '#f59e0b' : '#ef4444';

    return (
        <div>
            <div style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>System Health</h2>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
                    Pipeline reliability and system events · last {filters.days || 7} days
                </p>
            </div>

            {/* Health cards */}
            <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
                gap: 12, marginBottom: 28,
            }}>
                <MetricCard
                    label="Success Rate" value={successRate}
                    icon={ShieldCheck} accent={successColor}
                    format="percent" metricKey="success_rate"
                    onClick={setDrawerMetric}
                />
                <MetricCard
                    label="Error Rate" value={pipeline.error_rate_pct ?? 0}
                    icon={Bug} accent="#f59e0b"
                    format="percent" metricKey="error_rate"
                    onClick={setDrawerMetric}
                />
                <MetricCard
                    label="Failures" value={pipeline.failures ?? 0}
                    icon={AlertTriangle} accent={pipeline.failures > 0 ? '#ef4444' : '#10b981'}
                    metricKey="failures" onClick={setDrawerMetric}
                />
                <MetricCard
                    label="System Events" value={systemEvents.total_error_events ?? 0}
                    icon={Activity} accent="#8b5cf6"
                    sub={systemEvents.breakdown?.length ? `${systemEvents.breakdown.length} event types` : null}
                    metricKey="system_events" onClick={setDrawerMetric}
                />
            </div>

            {/* Generation time (honest: shows collecting / no_data label) */}
            <div style={{ marginBottom: 28, paddingTop: 16, borderTop: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <Clock size={14} style={{ color: '#8b5cf6' }} />
                    <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>
                        Generation Time
                    </h3>
                    <TelemetryBadge status={genTime?.telemetry_status} />
                </div>
                {genTime?.avg_ms != null ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
                        <MiniStat label="Avg Generation" value={`${Math.round(genTime.avg_ms).toLocaleString()} ms`} />
                        <MiniStat label="Median (P50)" value={genTime.p50_ms != null ? `${Math.round(genTime.p50_ms).toLocaleString()} ms` : '—'} />
                        <MiniStat label="Sample Count" value={genTime.sample_count || 0} />
                    </div>
                ) : (
                    <div style={{
                        display: 'flex', alignItems: 'flex-start', gap: 10,
                        padding: '12px 16px', borderRadius: 10,
                        background: '#f8fafc', border: '1px solid #e2e8f0',
                    }}>
                        <Info size={15} style={{ color: '#6b7280', marginTop: 1, flexShrink: 0 }} />
                        <div style={{ fontSize: 13, color: '#6b7280' }}>
                            {genTime?.note || 'Generation time data not yet collected.'}
                            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                                Emit <code style={{ background: '#f1f5f9', padding: '0 4px', borderRadius: 3 }}>generation_completed</code> events
                                with <code style={{ background: '#f1f5f9', padding: '0 4px', borderRadius: 3 }}>duration_ms</code> to populate.
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Pipeline summary */}
            <div style={{
                background: successRate >= 99 ? '#ecfdf5' : successRate >= 95 ? '#fffbeb' : '#fef2f2',
                borderRadius: 12, padding: '16px 20px', marginBottom: 24,
                border: `1px solid ${successColor}30`,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    {successRate >= 99 ? (
                        <ShieldCheck size={16} style={{ color: '#10b981' }} />
                    ) : (
                        <XCircle size={16} style={{ color: successColor }} />
                    )}
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                        {successRate >= 99 ? 'System Healthy' : successRate >= 95 ? 'Degraded Performance' : 'Elevated Error Rate'}
                    </span>
                </div>
                <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>
                    {pipeline.total_requests?.toLocaleString() || 0} requests · {pipeline.failures || 0} failures ·
                    {' '}{successRate}% success rate over the last {data?.period_days || 7} days
                </p>
            </div>

            {/* Error event breakdown */}
            <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 12 }}>
                    Error Event Breakdown
                </h3>
                <BreakdownTable
                    data={systemEvents.breakdown || []}
                    labelKey="event_name"
                    valueKey="count"
                    labelHeader="Event"
                    valueHeader="Count"
                    emptyMessage="No system error events in this period — that's good!"
                />
            </div>

            {/* Metric Drawer */}
            {drawerMetric && (
                <MetricDrawer
                    metricKey={drawerMetric}
                    filters={filters}
                    onClose={() => setDrawerMetric(null)}
                />
            )}

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default HealthTab;


// ── Helper components ─────────────────────────────────────────────────────────

const BADGE_CONFIG = {
    collecting: { label: 'Collecting',  bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
    no_data:    { label: 'No Data',     bg: '#f8fafc', color: '#64748b', border: '#e2e8f0' },
    partial:    { label: 'Partial',     bg: '#fef3c7', color: '#92400e', border: '#fde68a' },
    error:      { label: 'Error',       bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
};

const TelemetryBadge = ({ status }) => {
    if (!status || status === 'sufficient') return null;
    const cfg = BADGE_CONFIG[status] || BADGE_CONFIG.no_data;
    return (
        <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 7px',
            borderRadius: 4, background: cfg.bg,
            color: cfg.color, border: `1px solid ${cfg.border}`,
        }}>
            {cfg.label}
        </span>
    );
};

const MiniStat = ({ label, value }) => (
    <div style={{
        background: '#f8fafc', borderRadius: 10, padding: '14px 16px',
        border: '1px solid #f1f5f9',
    }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>
            {label}
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#111827', fontVariantNumeric: 'tabular-nums' }}>
            {value}
        </div>
    </div>
);
